import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import { Bell, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const BASE = (import.meta.env.BASE_URL || "/").replace(/\/$/, "") + "/api";
const DISMISS_KEY = "push_optin_dismissed_at";
const DISMISS_DAYS = 14;

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function detectDevice(): { deviceType: string; browser: string } {
  const ua = navigator.userAgent;
  const deviceType = /Mobi|Android|iPhone|iPad/i.test(ua) ? "mobile" : "desktop";
  let browser = "other";
  if (/Edg\//.test(ua)) browser = "edge";
  else if (/Chrome\//.test(ua)) browser = "chrome";
  else if (/Firefox\//.test(ua)) browser = "firefox";
  else if (/Safari\//.test(ua)) browser = "safari";
  return { deviceType, browser };
}

async function subscribeNow(): Promise<boolean> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    const sub = existing.toJSON();
    await fetch(`${BASE}/push/subscribe`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ endpoint: sub.endpoint, keys: sub.keys, ...detectDevice() }),
    });
    return true;
  }
  const r = await fetch(`${BASE}/push/vapid-public-key`);
  const { publicKey } = await r.json();
  if (!publicKey) return false;
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
  });
  const json = sub.toJSON();
  await fetch(`${BASE}/push/subscribe`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys, ...detectDevice() }),
  });
  return true;
}

export function PushOptIn() {
  const { lang } = useI18n();
  const t = (ar: string, en: string) => (lang === "ar" ? ar : en);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return;
    // Never prompt inside an iframe (mockup sandbox, theme previews, embeds).
    try { if (window.top !== window.self) return; } catch { return; }
    // Only prompt on the public storefront — never on admin or platform pages.
    const path = window.location.pathname;
    if (/\/admin(\/|$)/.test(path) || /\/platform(\/|$)/.test(path)) return;
    if (Notification.permission === "granted") {
      // Already granted — make sure we have a server-side subscription record.
      subscribeNow().catch(() => {});
      return;
    }
    if (Notification.permission === "denied") return;
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const ageMs = Date.now() - dismissedAt;
    if (dismissedAt && ageMs < DISMISS_DAYS * 24 * 60 * 60 * 1000) return;
    const timer = setTimeout(() => setShow(true), 8000);
    return () => clearTimeout(timer);
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm === "granted") {
        await subscribeNow();
        setShow(false);
      } else {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        setShow(false);
      }
    } catch {
      // fail quietly
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:end-4 sm:max-w-sm z-[60] bg-white border shadow-lg rounded-lg p-4 flex items-start gap-3">
      <div className="bg-orange-100 text-orange-600 rounded-full p-2 shrink-0">
        <Bell className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm">{t("اشترك في الإشعارات", "Stay in the loop")}</p>
        <p className="text-xs text-muted-foreground mt-1">
          {t("احصل على تنبيهات فورية بالعروض والمنتجات الجديدة.", "Get instant alerts about offers and new products.")}
        </p>
        <div className="flex gap-2 mt-3">
          <Button size="sm" onClick={enable} disabled={busy}
            className="bg-orange-500 hover:bg-orange-600 text-white border-0">
            {busy ? t("جارٍ...", "Working...") : t("تفعيل", "Enable")}
          </Button>
          <Button size="sm" variant="ghost" onClick={dismiss}>
            {t("لاحقاً", "Later")}
          </Button>
        </div>
      </div>
      <button onClick={dismiss} className="text-muted-foreground hover:text-foreground" aria-label={t("إغلاق", "Close")}>
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
